import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  type CSSObject,
  Step,
  stepClasses,
  stepConnectorClasses,
  type StepIconProps,
  StepLabel,
  stepLabelClasses,
  Stepper,
  type SxProps,
  Typography,
  useTheme
} from '@mui/material';
import { type Theme } from '@mui/material/styles';
import { mergeSx } from 'merge-sx';

import { TestDTO, TestSessionSummaryDTO } from '@/api/generated';
import CompletedIcon from '@/assets/icons/status-complete.svg?react';
import InProgressIcon from '@/assets/icons/status-in-progress.svg?react';
import IsRefusedIcon from '@/assets/icons/status-refused.svg?react';
import IsRegisteredIcon from '@/assets/icons/status-registered.svg?react';
import { useFormattedDatetime } from '@/hooks/useFormattedDatetime';
import { getTestSessionIconStyles, isComplete, isInProgress, isRefused } from '@/utils/testSessions';

import TestDateTimeSkeleton from './TestDateTimeSkeleton';

const TestSessionStepper = ({ testSession, sx }: { testSession: TestDTO & TestSessionSummaryDTO; sx?: SxProps }) => {
  const theme = useTheme();
  const { palette } = theme;
  const { t } = useTranslation(['common', 'tests']);
  const { formatDatetime } = useFormattedDatetime();

  return (
    <Stepper elevation={0} alternativeLabel activeStep={-1} sx={mergeSx(getStyles(theme), sx)}>
      <Step completed={!isRefused(testSession)}>
        <StepLabel StepIconComponent={StepIcon}>
          <Typography variant="body2" color={palette.grey[700]}>
            {t('fieldNames.createdAt', { ns: 'tests' })}
          </Typography>
          <Typography variant="body2" color={palette.text.primary}>
            {testSession.createdAt ? formatDatetime(testSession.createdAt) : <TestDateTimeSkeleton />}
          </Typography>
        </StepLabel>
      </Step>
      <Step
        completed={isInProgress(testSession) || isComplete(testSession)}
        active={isInProgress(testSession) || isRefused(testSession)}
      >
        <StepLabel StepIconComponent={StepIcon}>
          <Typography variant="body2" color={palette.grey[700]}>
            {t('fieldNames.startDate', { ns: 'tests' })}
          </Typography>
          <Typography variant="body2" color={palette.text.primary}>
            {testSession.startDate ? formatDatetime(testSession.startDate) : <TestDateTimeSkeleton />}
          </Typography>
        </StepLabel>
      </Step>
      <Step completed={isComplete(testSession)} active={isRefused(testSession)}>
        <StepLabel StepIconComponent={isRefused(testSession) ? IsRefusedStepIcon : StepIcon}>
          <Typography variant="body2" color={palette.grey[700]}>
            {t('fieldNames.endDate', { ns: 'tests' })}
          </Typography>
          <Typography variant="body2" color={palette.text.primary}>
            {testSession.endDate ? formatDatetime(testSession.endDate) : <TestDateTimeSkeleton />}
          </Typography>
        </StepLabel>
      </Step>
    </Stepper>
  );
};

export default TestSessionStepper;

const StepIcon = (props: StepIconProps) => {
  const icons: { [index: string]: React.ReactElement } = {
    1: <IsRegisteredIcon />,
    2: <InProgressIcon />,
    3: <CompletedIcon />
  };

  return <Box sx={getTestSessionIconStyles(props.completed)}>{icons[String(props.icon)]}</Box>;
};

const IsRefusedStepIcon = () => (
  <Box sx={getTestSessionIconStyles()}>
    <IsRefusedIcon />
  </Box>
);

const getStyles = ({ spacing, palette }: Theme): CSSObject => ({
  [`& .${stepClasses.root}`]: {
    padding: 0
  },

  [`& .${stepLabelClasses.label}`]: {
    marginTop: spacing(0.5)
  },

  [`& .${stepLabelClasses.iconContainer}`]: {
    display: 'block',
    width: spacing(1.5),
    height: spacing(1.5)
  },

  [`& .${stepConnectorClasses.root}`]: {
    top: spacing(0.75),
    transform: 'translateY(-50%)'
  },

  [`.${stepConnectorClasses.root}.${stepConnectorClasses.completed} .${stepConnectorClasses.line}`]: {
    borderColor: palette.primary[500]
  },

  // if step is active, but not completed, color error
  [`.${stepConnectorClasses.root}.${stepConnectorClasses.active}:not(.${stepConnectorClasses.completed})
    .${stepConnectorClasses.line}`]: {
    borderColor: palette.error.main
  },

  [`& .${stepConnectorClasses.line}`]: {
    borderTopWidth: spacing(0.5),
    borderColor: palette.primary[200]
  },

  [`& .${stepClasses.root}:first-of-type`]: {
    padding: 0,

    [`& + .${stepClasses.root} .${stepConnectorClasses.root}`]: {
      insetInlineStart: `calc(-100% + ${spacing(1.75)})`,
      insetInlineEnd: `calc(50% + ${spacing(1)})`
    },

    [`& .${stepLabelClasses.root}`]: {
      alignItems: 'flex-start'
    },

    [`& .${stepLabelClasses.label}`]: {
      textAlign: 'start'
    }
  },

  [`& .${stepClasses.root}:last-of-type`]: {
    padding: 0,

    [`& .${stepConnectorClasses.root}`]: {
      insetInlineStart: `calc(-50% + ${spacing(1)})`,
      insetInlineEnd: spacing(1.75)
    },

    [`& .${stepLabelClasses.root}`]: {
      alignItems: 'flex-end'
    },

    [`& .${stepLabelClasses.label}`]: {
      textAlign: 'end'
    }
  }
});
